import {
  collection, doc, addDoc, getDocs, updateDoc,
  query, where, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  IEmailVerificationRepository, CreateEmailVerificationData, GetEmailVerificationData,
} from './interfaces/email-verification.repository.interface';

export class EmailVerificationRepository implements IEmailVerificationRepository {
  private collectionName = 'emailVerifications';

  async createEmailVerification(verificationData: CreateEmailVerificationData): Promise<GetEmailVerificationData> {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...verificationData,
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
      });
      return {
        id: docRef.id,
        ...verificationData,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000),
        createdAt: new Date(),
      } as unknown as GetEmailVerificationData;
    } catch (error) {
      throw new DRXError({
        code: 'EMAIL_VERIFICATION_CREATE_FAILED',
        devMessage: 'Failed to create email verification',
        cause: error,
      });
    }
  }

  async verifyEmail(token: string): Promise<{ success: boolean; userId: string }> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('token', '==', token),
        where('usedAt', '==', null),
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new DRXError({
          code: 'EMAIL_VERIFICATION_INVALID_TOKEN',
          devMessage: `Invalid or expired verification token: ${token}`,
          userMessage: 'Invalid or expired verification link.',
          level: 'warning',
        });
      }

      const verificationDoc = snapshot.docs[0];
      const verificationData = verificationDoc.data();

      if (verificationData.expiresAt.toDate() < new Date()) {
        throw new DRXError({
          code: 'EMAIL_VERIFICATION_EXPIRED',
          devMessage: `Verification token expired: ${token}`,
          userMessage: 'This verification link has expired. Please request a new one.',
          level: 'warning',
        });
      }

      // Mark as used
      await updateDoc(verificationDoc.ref, { usedAt: serverTimestamp() });

      // Update user as verified
      await updateDoc(doc(db, 'users', verificationData.userId), {
        emailVerified: true,
        updatedAt: serverTimestamp(),
      });

      return { success: true, userId: verificationData.userId };
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EMAIL_VERIFICATION_FAILED',
        devMessage: `Failed to verify email with token ${token}`,
        cause: error,
      });
    }
  }
}
